<!-- Loading State -->
<div *ngIf="isLoading()" class="d-flex justify-content-center align-items-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Error State -->
<div *ngIf="!isLoading() && errorMessage()" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>
  {{ errorMessage() }}
  <button class="btn btn-link p-0 ms-2" (click)="goBack()">Return to Drones</button>
</div>

<!-- Content -->
<ng-container *ngIf="!isLoading() && !errorMessage() && drone()">
  <!-- Page Header -->
  <div class="d-flex justify-content-between align-items-start mb-4">
    <div>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-1">
          <li class="breadcrumb-item"><a routerLink="/drones">Drones</a></li>
          <li class="breadcrumb-item active">{{ drone()!.name }}</li>
        </ol>
      </nav>
      <div class="d-flex align-items-center gap-2">
        <h1 class="h2 mb-0 fw-bold">{{ drone()!.name }}</h1>
        <span class="badge" [ngClass]="getOwnershipClass(drone()!.ownership)">
          {{ formatOwnership(drone()!.ownership) }}
        </span>
        <span class="badge" [ngClass]="getStatusClass(drone()!.status)">
          {{ formatStatus(drone()!.status) }}
        </span>
      </div>
    </div>
    <div class="btn-group">
      <button class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
      <button class="btn btn-outline-primary" (click)="editDrone()"
              [disabled]="!canEdit()"
              [title]="canEdit() ? 'Edit drone' : 'ADN drones cannot be edited'">
        <i class="bi bi-pencil me-2"></i>Edit
      </button>
      <button class="btn btn-outline-danger" (click)="confirmDelete()"
              [disabled]="!canDelete()"
              [title]="canDelete() ? 'Delete drone' : 'ADN drones cannot be deleted'">
        <i class="bi bi-trash me-2"></i>Delete
      </button>
    </div>
  </div>

  <!-- ADN Info Alert -->
  <div *ngIf="isAdnDrone()" class="alert alert-info d-flex align-items-center mb-4">
    <i class="bi bi-info-circle me-2"></i>
    <span>This drone is leased from American Drone Network and cannot be edited or deleted. Contact ADN for any changes.</span>
  </div>

  <div class="row g-4">
    <!-- Left Column - Main Content -->
    <div class="col-lg-8">
      <!-- Drone Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="icon-drone me-2"></i>Drone Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Manufacturer & Model</label>
              <p class="mb-0 fw-medium">{{ drone()!.manufacturerName }} {{ drone()!.modelName }}</p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Serial Number</label>
              <p class="mb-0"><code>{{ drone()!.serialNumber }}</code></p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">FAA Registration (N-Number)</label>
              <p class="mb-0">
                <ng-container *ngIf="drone()!.registrationNumber; else noReg">
                  {{ drone()!.registrationNumber }}
                </ng-container>
                <ng-template #noReg>
                  <span class="text-muted fst-italic">Not registered</span>
                </ng-template>
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Remote ID</label>
              <p class="mb-0">
                <ng-container *ngIf="drone()!.remoteId; else noRemoteId">
                  {{ drone()!.remoteId }}
                </ng-container>
                <ng-template #noRemoteId>
                  <span class="text-muted fst-italic">Not configured</span>
                </ng-template>
              </p>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small mb-1">Firmware Version</label>
              <p class="mb-0">
                <ng-container *ngIf="drone()!.firmwareVersion; else noFirmware">
                  {{ drone()!.firmwareVersion }}
                </ng-container>
                <ng-template #noFirmware>
                  <span class="text-muted fst-italic">Unknown</span>
                </ng-template>
              </p>
            </div>
            <div class="col-md-6" *ngIf="isAdnDrone()">
              <label class="form-label text-muted small mb-1">Assigned Date</label>
              <p class="mb-0">{{ formatDate(drone()!.assignedAt) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- DJI connection (ADN-2307): only for DJI-built drones -->
      <app-dji-connection-card *ngIf="isDjiDrone()" [droneId]="drone()!.id" [droneName]="drone()!.name ?? ''"></app-dji-connection-card>

      <!-- Flight Statistics -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-graph-up me-2"></i>Flight Statistics</h5>
          <button class="btn btn-outline-secondary btn-sm" (click)="viewFlightHistory()"
                  [disabled]="!hasFlightHistory()">
            <i class="bi bi-list-ul me-1"></i>View Flight History
          </button>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12 col-sm-6 col-lg-3">
              <div class="text-center">
                <div class="fs-3 fw-bold text-primary">{{ droneStats().totalFlights }}</div>
                <div class="small text-muted">Total Flights</div>
              </div>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <div class="text-center">
                <div class="fs-3 fw-bold text-success">{{ droneStats().totalAcresSprayed | number:'1.0-0' }}</div>
                <div class="small text-muted">Acres Sprayed</div>
              </div>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <div class="text-center">
                <div class="fs-3 fw-bold text-info">{{ droneStats().totalFlightHours | number:'1.1-1' }}</div>
                <div class="small text-muted">Flight Hours</div>
              </div>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <div class="text-center">
                <div class="fs-5 fw-medium text-body-secondary">
                  {{ droneStats().lastFlightDate ? (droneStats().lastFlightDate | adnDate) : 'Never' }}
                </div>
                <div class="small text-muted">Last Flight</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Maintenance History -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-tools me-2"></i>Maintenance History</h5>
          <button class="btn btn-outline-primary btn-sm" (click)="addMaintenanceRecord()">
            <i class="bi bi-plus-lg me-1"></i>Add Record
          </button>
        </div>
        <div class="card-body">
          <ng-container *ngIf="recentMaintenance().length > 0; else noMaintenance">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let record of recentMaintenance()">
                    <td>{{ formatDate(record.scheduledDate) }}</td>
                    <td>
                      <span class="badge bg-secondary-subtle text-secondary">
                        {{ formatMaintenanceType(record.type) }}
                      </span>
                    </td>
                    <td>{{ record.title }}</td>
                    <td>
                      <span class="badge" [ngClass]="getMaintenanceStatusClass(record.status)">
                        {{ formatMaintenanceStatus(record.status) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button class="btn btn-outline-secondary btn-sm" (click)="editMaintenanceRecord(record)" title="Edit">
                        <i class="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div *ngIf="maintenanceRecords().length > 5" class="text-center mt-3">
              <button class="btn btn-link btn-sm">View All {{ maintenanceRecords().length }} Records</button>
            </div>
          </ng-container>
          <ng-template #noMaintenance>
            <div class="text-center py-4">
              <div class="text-muted mb-2">
                <i class="bi bi-tools fs-1"></i>
              </div>
              <p class="text-muted mb-3">No maintenance records yet.</p>
              <button class="btn btn-outline-primary btn-sm" (click)="addMaintenanceRecord()">
                <i class="bi bi-plus-lg me-1"></i>Add First Record
              </button>
            </div>
          </ng-template>
        </div>
      </div>

      <!-- Documents -->
      <div class="card mt-4 mb-4 mb-lg-0">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Documents</h5>
          <button class="btn btn-outline-primary btn-sm" (click)="openResourceModal()">
            <i class="bi bi-plus-lg me-1"></i>Attach Document
          </button>
        </div>
        <div class="card-body">
          <div class="row g-3 align-items-end mb-3">
            <div class="col-md-5">
              <label for="resourceTypeFilter" class="form-label fw-semibold">Resource Type</label>
              <select
                id="resourceTypeFilter"
                class="form-select"
                [ngModel]="resourceTypeFilter()"
                (ngModelChange)="resourceTypeFilter.set($event); loadResources()"
              >
                @for (type of resourceTypes; track type) {
                  <option [value]="type">
                    {{ type === 'all' ? 'All' : (type | removeDash | titlecase) }}
                  </option>
                }
              </select>
            </div>
          </div>

          @if (resourcesError()) {
            <div class="alert alert-danger alert-dismissible fade show" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ resourcesError() }}
              <button type="button" class="btn-close" aria-label="Close" (click)="resourcesError.set(null)"></button>
            </div>
          }

          @if (loadingResources()) {
            <div class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          } @else if (resources().length) {
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Type</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (resource of resources(); track resource.id) {
                    <tr>
                      <td>
                        <div class="resource-info">
                          <div class="resource-avatar">
                            <i class="bi bi-file-earmark"></i>
                          </div>
                          <div>
                            <div class="fw-medium">{{ resource.fileName }}</div>
                            @if (resource.caption) {
                              <div class="text-muted small">{{ resource.caption }}</div>
                            }
                            @if (resource.fileSize) {
                              <div class="text-muted small">{{ formatFileSize(resource.fileSize) }}</div>
                            }
                          </div>
                        </div>
                      </td>
                      <td>
                        <span>{{ resource.resourceType | removeDash | titlecase }}</span>
                      </td>
                      <td class="text-end text-nowrap">
                        <div class="d-flex justify-content-end gap-1">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          title="Open document"
                          aria-label="Open document"
                          (click)="openResource(resource)">
                          <i class="bi bi-file-earmark-arrow-down"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-danger btn-sm"
                          title="Delete document"
                          aria-label="Delete document"
                          (click)="confirmResourceDelete(resource)">
                          <i class="bi bi-trash"></i>
                        </button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="text-center py-4">
              <div class="text-muted mb-2">
                <i class="bi bi-file-earmark-text fs-1"></i>
              </div>
              <p class="text-muted mb-3">No documents for this drone yet.</p>
              <button class="btn btn-outline-primary btn-sm" (click)="openResourceModal()">
                <i class="bi bi-plus-lg me-1"></i>Attach First Document
              </button>
            </div>
          }
        </div>
      </div>

      <!--Generate Drone Report Section (Owner, Chief Supervisor, PIC only) -->
      @if (canGenerateReports()) {
      <div class="card mt-4 mb-4 mb-lg-0">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Generate Drone Report</h5>
          </div>
        </div>
        <div class="card-body">
          <!-- Error message for report generation -->
          <div *ngIf="reportError()" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            {{ reportError() }}
            <button type="button" class="btn-close" (click)="clearError()"></button>
            <div class="mt-2">
              <button class="btn btn-sm btn-outline-danger" (click)="retryLoad()">
                <i class="bi bi-arrow-clockwise me-1"></i>Retry
              </button>
            </div>
          </div>

            <h6 class="section-title mb-2">Report Options</h6>
            <p class="small text-muted mb-3">
              Create a PDF report for this drone. You can specify a date range to include only the relevant data.
            </p>
            <!-- Date Range -->
            <div class="row g-3 mb-4">
              <div class="col-6">
                <label class="form-label">Start Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="reportStartDate"
                  [class.is-invalid]="reportStartDate && reportEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-6">
                <label class="form-label">End Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="reportEndDate"
                  [class.is-invalid]="reportStartDate && reportEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-12" *ngIf="reportStartDate && reportEndDate && !isDateRangeValid()">
                <div class="invalid-feedback d-block">
                  <i class="bi bi-exclamation-circle me-1"></i>
                  End date must be on or after start date
                </div>
              </div>
            </div>

            <!-- Generate Button -->
            <div class="d-grid">
              <!-- Generate Button -->
              <button
                class="btn btn-primary w-100"
                (click)="generateReport()"
                [disabled]="isGenerating() || !isDateRangeValid()"
              >
                <ng-container *ngIf="!isGenerating()">
                  <i class="bi bi-file-earmark-pdf me-2"></i>Generate PDF Report
                </ng-container>
                <ng-container *ngIf="isGenerating()">
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Generating...
                </ng-container>
              </button>
            </div>

            <!-- Recent Reports -->
            <div class="card mt-4">
              <div class="card-header d-flex justify-content-between align-items-center p-3">
                <h5 class="mb-0">Recent Reports</h5>
                <span class="badge bg-secondary">{{ recentReports().length }} reports</span>
              </div>
              <div class="card-body p-0">
                <!-- Loading State -->
                <ng-container *ngIf="isLoading()">
                  <div class="p-5 text-center">
                    <div class="spinner-border text-primary mb-3" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="text-muted mb-0">Loading reports...</p>
                  </div>
                </ng-container>

                <!-- Reports List -->
                <ng-container *ngIf="!isLoading()">
                  <ng-container *ngIf="recentReports().length > 0; else emptyState">
                    <app-drone-report-item
                      *ngFor="let report of recentReports(); let last = last"
                      class="d-block"
                      [class.border-bottom]="!last"
                      [report]="report"
                      (download)="downloadReport(report)"
                      (retry)="retryReport(report)"
                      (remove)="reportToDelete.set($event)">
                    </app-drone-report-item>
                  </ng-container>
                 
                  <!-- Empty State -->
                  <ng-template #emptyState>
                    <div class="p-5 text-center">
                      <div class="empty-state-icon mb-3">
                        <i class="bi bi-file-earmark-text"></i>
                      </div>
                      <h5>No Reports Generated</h5>
                      <p class="text-muted mb-0">Generate your first drone report using the form above.</p>
                      <button class="btn btn-sm btn-outline-primary mt-3"
                        (click)="generateReport()"
                        [disabled]="isGenerating() || !isDateRangeValid()">
                        <ng-container *ngIf="!isGenerating()">
                          <i class="bi bi-plus-lg me-1"></i>Generate Report
                        </ng-container>
                        <ng-container *ngIf="isGenerating()">
                          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                          Generating...
                        </ng-container>
                      </button>
                    </div>
                  </ng-template>

                </ng-container>
              </div>
            </div>
          </div>
      </div>
      }
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Status Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Status</h5>
        </div>
        <div class="card-body text-center py-4">
          <div class="mb-3">
            <span class="badge fs-5 px-4 py-2" [ngClass]="getStatusClass(drone()!.status)">
              {{ formatStatus(drone()!.status) }}
            </span>
          </div>
          <p class="text-muted small mb-0">
            <ng-container *ngIf="drone()!.status === 'active'">
              This drone is operational and ready for flights.
            </ng-container>
            <ng-container *ngIf="drone()!.status === 'maintenance'">
              This drone is currently undergoing maintenance.
            </ng-container>
            <ng-container *ngIf="drone()!.status === 'inactive'">
              This drone is temporarily inactive.
            </ng-container>
            <ng-container *ngIf="drone()!.status === 'retired'">
              This drone has been retired from service.
            </ng-container>
          </p>
        </div>
      </div>

      <!-- Upcoming Maintenance -->
      <div class="card mb-4" *ngIf="upcomingMaintenance()">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Upcoming Maintenance</h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <span class="fw-medium">{{ upcomingMaintenance()!.title }}</span>
            <span class="badge" [ngClass]="getMaintenanceStatusClass(upcomingMaintenance()!.status)">
              {{ formatMaintenanceStatus(upcomingMaintenance()!.status) }}
            </span>
          </div>
          <p class="small text-muted mb-0">
            <i class="bi bi-calendar me-1"></i>
            Scheduled: {{ formatDate(upcomingMaintenance()!.scheduledDate) }}
          </p>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">Quick Stats</h5>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Total Flights</span>
            <span class="fw-medium">{{ droneStats().totalFlights }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Acres Sprayed</span>
            <span class="fw-medium">{{ droneStats().totalAcresSprayed | number:'1.0-0' }} ac</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-muted">Flight Hours</span>
            <span class="fw-medium">{{ droneStats().totalFlightHours | number:'1.1-1' }} hrs</span>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Ownership</span>
            <span class="badge" [ngClass]="getOwnershipClass(drone()!.ownership)">
              {{ drone()!.ownership === 'adn' ? 'ADN' : 'Owned' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button class="btn btn-outline-primary" (click)="editDrone()"
                    [disabled]="!canEdit()">
              <i class="bi bi-pencil me-2"></i>Edit Drone
            </button>
            <button class="btn btn-outline-secondary" (click)="viewFlightHistory()"
                    [disabled]="!hasFlightHistory()">
              <i class="bi bi-list-ul me-2"></i>View Flight History
            </button>
            <button class="btn btn-outline-secondary" (click)="goBack()">
              <i class="bi bi-arrow-left me-2"></i>Back to Drones
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>

<!-- Document Delete Confirmation -->
<app-confirm-modal
  #resourceConfirmModal
  (confirmed)="onResourceDeleteConfirmed()"
  (cancelled)="resourceToDelete.set(null)"
></app-confirm-modal>

<!-- Attach Document Modal -->
@if (showResourceModal()) {
  <app-drone-resource-modal
    [droneId]="droneId() || ''"
    [droneName]="drone()?.name || ''"
    (close)="closeResourceModal()"
    (newItem)="onResourceUploaded()"
  ></app-drone-resource-modal>
}

<!-- Maintenance Modal -->
<app-maintenance-modal
  #maintenanceModal
  [droneId]="droneId() || ''"
  [droneName]="drone()?.name || ''"
  (saved)="onMaintenanceSaved($event)"
  (cancelled)="onMaintenanceCancelled()"
></app-maintenance-modal>

<!-- Delete Report Confirmation (open while reportToDelete is set) -->
@if (reportToDelete(); as report) {
  <app-drone-report-confirm-modal
    [report]="report"
    [action]="deleteReport"
    (closed)="reportToDelete.set(null)" />
}
